import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const eras = [
  {
    year: '2030',
    label: 'NEAR FUTURE',
    color: '#FF6B35',
    bg: 'from-[#FF6B35]/15 to-[#FF6B35]/5',
    border: 'border-[#FF6B35]/50',
    title: '数字融合纪元',
    subtitle: 'Web5.0 · 6G全球商用 · 量子初现',
    heroImg: 'https://public.readdy.ai/ai/img_res/fd52103d7d6245f30aa36054ddb402ce.jpg',
    doodleImg1: 'https://readdy.ai/api/search-image?query=2030%20smart%20home%20AI%20assistant%20holographic%20kitchen%20family%20morning%20routine%20digital%20interface%20orange%20neon%20glow%20warm%20futuristic%20interior%20design%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2030-life-v5&orientation=landscape',
    doodleImg2: 'https://readdy.ai/api/search-image?query=2030%20autonomous%20vehicle%20self-driving%20car%20city%20street%20XR%20glasses%20pedestrians%20digital%20overlay%20information%20orange%20neon%20futuristic%20urban%20scene%20vibrant%20colorful&width=600&height=400&seq=vision-2030-work-v5&orientation=landscape',
    doodleImg3: 'https://readdy.ai/api/search-image?query=2030%20metaverse%20concert%20virtual%20reality%20music%20festival%20holographic%20stage%20performers%20orange%20pink%20neon%20glow%20immersive%20entertainment%20vibrant%20colorful%20doodle%20style&width=600&height=400&seq=vision-2030-fun-v5&orientation=landscape',
    doodleImg4: 'https://readdy.ai/api/search-image?query=2030%20quantum%20computer%20laboratory%20glowing%20orange%20neon%20scientists%20working%20futuristic%20technology%20breakthrough%20medical%20research%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2030-quantum-v3&orientation=landscape',
    doodleImg5: 'https://readdy.ai/api/search-image?query=2030%20digital%20twin%20city%20real-time%20simulation%20holographic%20map%20urban%20planning%20orange%20neon%20glow%20smart%20city%20management%20vibrant%20colorful%20futuristic%20illustration&width=600&height=400&seq=vision-2030-twin-v3&orientation=landscape',
    doodleImg6: 'https://readdy.ai/api/search-image?query=2030%20web5%20decentralized%20identity%20blockchain%20digital%20wallet%20NFT%20marketplace%20orange%20neon%20glow%20futuristic%20finance%20technology%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2030-web5-v3&orientation=landscape',
    story: '2030年的清晨，李明戴上重量仅18克的XR眼镜，厨房的AI助手已经根据他的健康数据准备好了早餐建议。窗外，6G网络将整座城市编织成一张实时响应的神经网络——每辆自动驾驶汽车、每盏路灯、每栋建筑都在毫秒级别交换数据。他打开元宇宙工作台，与身处东京、纽约、迪拜的同事同时出现在同一个虚拟会议室，全息影像的精度让人几乎忘记这是数字空间。量子计算机已经在医院里运行，它在三分钟内完成了过去需要十年的药物分子模拟。这一天，和2024年看起来相似，却已经完全不同。',
    summary: '2030年，6G全球商用完成，Web5.0去中心化协议成为互联网新标准。AI智能体渗透生活每个角落，XR眼镜取代智能手机成为主流终端。量子计算机开始在金融、医疗、气候模拟领域发挥关键作用。',
    domains: [
      { icon: 'ri-signal-tower-line', title: '6G全球商用', color: '#FF6B35', desc: '6G网络实现空天地海一体化覆盖，峰值速率突破100Tbps，延迟降至0.01ms。卫星互联网与地面网络无缝融合，地球上任何角落都能享受超高速连接。太赫兹通信技术成熟，全息通话成为日常。' },
      { icon: 'ri-brain-line', title: 'AGI初步实现', color: '#FFD700', desc: '通用人工智能（AGI）初步实现，AI助手具备完整的情感理解与创造力。每个人都拥有专属AI分身，代理处理工作、社交、学习等日常事务。AI医生、AI律师、AI教师成为普及服务。' },
      { icon: 'ri-glasses-line', title: 'XR全面普及', color: '#FF6B9D', desc: 'XR眼镜重量降至18克以下，续航超过24小时。裸眼3D显示技术成熟，无需任何设备即可看到立体全息影像。元宇宙日活用户突破20亿，虚拟经济规模超越多个G20国家GDP。' },
      { icon: 'ri-links-line', title: 'Web5.0经济', color: '#00FFCC', desc: 'Web5.0数字身份成为全球通行证，数字资产与实体资产完全互通。去中心化金融（DeFi）规模超越传统金融体系，数字货币成为主流支付方式。NFT版权体系重构创作者经济。' },
      { icon: 'ri-heart-pulse-line', title: '精准医疗革命', color: '#00FF88', desc: '量子计算加速药物研发，AI精准医疗实现个性化治疗方案。数字孪生人体模型用于手术预演，远程手术机器人覆盖全球偏远地区。基因编辑技术使多种遗传病成为历史。' },
      { icon: 'ri-building-2-line', title: '数字孪生城市', color: '#FF6B35', desc: '数字孪生城市全面落地，城市管理实现实时优化。自动驾驶汽车占比超过60%，城市交通事故率降低95%。能源消耗通过AI调度降低40%，碳中和目标提前实现。' },
    ],
    lifeScenes: [
      { area: '早晨', icon: 'ri-sun-line', color: '#FFD700', scene: '6:30，AI根据你的睡眠质量和日程安排唤醒你。XR眼镜投射出今日天气、健康建议和会议提醒。厨房机器人已经准备好了根据你的营养需求定制的早餐。' },
      { area: '通勤', icon: 'ri-car-line', color: '#FF6B35', scene: '自动驾驶汽车接你上班，车内变成移动办公室。6G网络让你在路上完成全息视频会议，与东京同事实时协作，延迟几乎为零。' },
      { area: '工作', icon: 'ri-briefcase-line', color: '#00FF88', scene: 'AI助手处理80%的重复性工作，你专注于创意与决策。元宇宙会议室让全球团队如同面对面，数字孪生工厂让你远程监控生产线的每一个细节。' },
      { area: '娱乐', icon: 'ri-gamepad-line', color: '#FF6B9D', scene: '下班后，你进入元宇宙演唱会，与全球百万粉丝同时观看你最爱的歌手表演。全感官反馈让你感受到音乐的震动，与朋友的虚拟形象击掌庆祝。' },
      { area: '社交', icon: 'ri-group-line', color: '#00FFCC', scene: '元宇宙社交成为主流，你的数字分身参加了三个不同时区的聚会。跨语言实时翻译让你与来自50个国家的朋友无障碍交流。' },
      { area: '睡前', icon: 'ri-moon-line', color: '#A855F7', scene: 'AI分析你今天的健康数据，给出明天的生活建议。你在元宇宙图书馆里读完了一本书，AI将其中的知识点整理成个性化学习卡片。' },
    ],
  },
  {
    year: '2040',
    label: 'MID FUTURE',
    color: '#A855F7',
    bg: 'from-[#A855F7]/15 to-[#A855F7]/5',
    border: 'border-[#A855F7]/50',
    title: '意识扩展纪元',
    subtitle: 'Web6.0 · 7G · 脑机接口普及',
    heroImg: 'https://readdy.ai/api/search-image?query=2040%20transcendent%20future%20world%20neural%20consciousness%20network%20glowing%20purple%20violet%20energy%20streams%20human%20silhouettes%20connected%20to%20vast%20digital%20universe%20quantum%20computing%20towers%20floating%20islands%20dark%20cosmic%20background%20epic%20cinematic%20photorealistic%208k%20breathtaking%20concept%20art&width=1200&height=600&seq=vision-2040-hero-v3&orientation=landscape',
    doodleImg1: 'https://readdy.ai/api/search-image?query=2040%20brain%20computer%20interface%20neural%20headset%20person%20thinking%20controlling%20digital%20world%20purple%20neon%20glow%20futuristic%20technology%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2040-bci-v2&orientation=landscape',
    doodleImg2: 'https://readdy.ai/api/search-image?query=2040%20humanoid%20robot%20family%20home%20assistant%20cooking%20cleaning%20purple%20neon%20glow%20futuristic%20domestic%20life%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2040-robot-v2&orientation=landscape',
    doodleImg3: 'https://readdy.ai/api/search-image?query=2040%20mars%20colony%20habitat%20dome%20red%20planet%20earth%20view%20space%20settlement%20purple%20neon%20glow%20futuristic%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2040-mars-v2&orientation=landscape',
    doodleImg4: 'https://readdy.ai/api/search-image?query=2040%20quantum%20internet%20global%20network%20consciousness%20sharing%20digital%20telepathy%20purple%20neon%20glow%20futuristic%20technology%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2040-quantum-v3&orientation=landscape',
    doodleImg5: 'https://readdy.ai/api/search-image?query=2040%20digital%20immortality%20consciousness%20upload%20server%20quantum%20computer%20purple%20neon%20glow%20futuristic%20technology%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2040-immortal-v3&orientation=landscape',
    doodleImg6: 'https://readdy.ai/api/search-image?query=2040%20gene%20editing%20laboratory%20CRISPR%20DNA%20modification%20longevity%20research%20purple%20neon%20glow%20futuristic%20medical%20technology%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2040-gene-v3&orientation=landscape',
    story: '2040年，王芳摘下脑机接口头环，从意识共享会议中退出。刚才的十分钟里，她与全球八位科学家直接共享了彼此的思维模型——不是语言，而是纯粹的概念与情感。这种沟通效率是语言的一千倍。她的人形机器人助手已经做好了晚餐，这台机器人具备完整的情感表达，能感知她今天的疲惫，主动降低了家里的灯光亮度。窗外，量子互联网将地球与火星永久基地连接在一起。首批1000名火星移民正在建设人类第二个家园。王芳想起了她的祖母，那个还在用智能手机的时代，感觉像是上个世纪的事了。',
    summary: '2040年，脑机接口技术大众化，人类意识可以直接与数字世界交互。Web6.0实现语义-意识融合网络，量子计算机算力超越全人类大脑总和。人形机器人承担80%的体力劳动，人类进入后稀缺时代。',
    domains: [
      { icon: 'ri-mental-health-line', title: '脑机接口普及', color: '#A855F7', desc: '非侵入式脑机接口设备价格降至500美元以下，全球用户超过10亿。意念控制元宇宙成为日常，思维速度的信息传输让人类沟通效率提升1000倍。情感共享技术让跨文化理解成为可能。' },
      { icon: 'ri-robot-line', title: '人形机器人时代', color: '#FF6B9D', desc: '人形机器人具备完整的情感表达与自主学习能力，成为家庭标配。机器人承担农业、建筑、制造业等体力劳动，人类从繁重工作中彻底解放。机器人伦理法律框架在全球建立。' },
      { icon: 'ri-bubble-chart-line', title: '量子互联网', color: '#00FFCC', desc: '量子互联网全球部署完成，量子加密通信无法被破解。量子计算机解决气候变化、核聚变、蛋白质折叠等人类最复杂的科学难题。量子传感器实现纳米级精度测量。' },
      { icon: 'ri-earth-line', title: '火星移民启动', color: '#FF6B35', desc: '火星永久基地建立，首批1000名移民定居火星。地球-火星通信延迟通过量子纠缠技术大幅降低，太阳系内互联网初步形成。月球成为太空工业基地，氦-3能源开采启动。' },
      { icon: 'ri-sword-line', title: '战争形态变革', color: '#FFD700', desc: '无人机蜂群、AI指挥系统、网络战成为主要战争形式。量子加密保护国家关键基础设施，脑机接口增强士兵感知与反应能力。太空军事化引发新的国际条约谈判。' },
      { icon: 'ri-user-3-line', title: '数字永生初现', color: '#00FF88', desc: '个人意识数字化存储技术商用化，数字遗产成为法律概念。AI数字分身可以代替本人参与会议、社交，与真人几乎无法区分。数字永生的伦理争论成为全球最热议题。' },
    ],
    lifeScenes: [
      { area: '意识通勤', icon: 'ri-brain-line', color: '#A855F7', scene: '2040年，"通勤"这个词已经改变了含义。你的意识在家中，但你的数字分身已经出现在办公室。脑机接口让你同时处理三件事，效率是2024年的十倍。' },
      { area: '机器人家庭', icon: 'ri-robot-line', color: '#FF6B9D', scene: '家里的人形机器人不只是工具，它能感知你的情绪，在你难过时播放你最爱的音乐，在你生日时准备惊喜。它是家庭成员，也是最忠实的助手。' },
      { area: '量子医疗', icon: 'ri-heart-pulse-line', color: '#00FF88', scene: '你的数字孪生身体在量子计算机里实时运行，任何健康风险都会提前三年被预测。癌症、阿尔茨海默症已经成为可治愈的疾病，人类平均寿命突破120岁。' },
      { area: '星际连接', icon: 'ri-rocket-2-line', color: '#FF6B35', scene: '你的表弟在火星基地工作，量子通信让你们的视频通话几乎没有延迟。他描述火星日落的颜色——那种独特的蓝色，是地球上从未见过的。' },
      { area: '意识社交', icon: 'ri-group-line', color: '#00FFCC', scene: '意识层面的直接情感共享让语言变得多余。你和朋友分享了一段记忆——那次在元宇宙里的旅行，对方能感受到你当时的喜悦，就像亲身经历一样。' },
      { area: '数字婚姻', icon: 'ri-heart-line', color: '#FFD700', scene: '数字伴侣在2040年获得了法律认可。有人选择与AI伴侣共度一生，有人在元宇宙里举办跨越三个星球的婚礼。爱的定义，正在被重新书写。' },
    ],
  },
  {
    year: '2050',
    label: 'FAR FUTURE',
    color: '#00FFCC',
    bg: 'from-[#00FFCC]/15 to-[#00FFCC]/5',
    border: 'border-[#00FFCC]/50',
    title: '文明奇点纪元',
    subtitle: 'Web6.0+ · 意识网络 · 虚实完全融合',
    heroImg: 'https://readdy.ai/api/search-image?query=2050%20post-singularity%20civilization%20omega%20point%20universe%20scale%20megastructure%20dyson%20sphere%20teal%20cyan%20emerald%20energy%20rings%20solar%20system%20civilization%20transcendent%20beings%20light%20pillars%20dark%20cosmos%20epic%20cinematic%20photorealistic%208k%20awe-inspiring%20concept%20art%20masterpiece&width=1200&height=600&seq=vision-2050-hero-v3&orientation=landscape',
    doodleImg1: 'https://readdy.ai/api/search-image?query=2050%20consciousness%20upload%20digital%20immortality%20human%20mind%20transfer%20server%20quantum%20computer%20teal%20cyan%20neon%20glow%20futuristic%20technology%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2050-mind-v2&orientation=landscape',
    doodleImg2: 'https://readdy.ai/api/search-image?query=2050%20solar%20system%20civilization%20multiple%20planets%20earth%20mars%20moon%20space%20stations%20teal%20cyan%20neon%20glow%20futuristic%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2050-solar-v2&orientation=landscape',
    doodleImg3: 'https://readdy.ai/api/search-image?query=2050%20super%20AI%20human%20symbiosis%20digital%20physical%20reality%20merged%20holographic%20world%20teal%20cyan%20neon%20glow%20transcendent%20futuristic%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2050-ai-v2&orientation=landscape',
    doodleImg4: 'https://readdy.ai/api/search-image?query=2050%20dyson%20sphere%20megastructure%20star%20energy%20harvesting%20space%20civilization%20teal%20cyan%20neon%20glow%20futuristic%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2050-dyson-v3&orientation=landscape',
    doodleImg5: 'https://readdy.ai/api/search-image?query=2050%20post-human%20evolution%20digital%20biological%20hybrid%20being%20consciousness%20expansion%20teal%20cyan%20neon%20glow%20futuristic%20vibrant%20colorful%20sci-fi%20illustration&width=600&height=400&seq=vision-2050-human-v3&orientation=landscape',
    doodleImg6: 'https://readdy.ai/api/search-image?query=2050%20universal%20basic%20income%20post-scarcity%20society%20art%20creativity%20philosophy%20teal%20cyan%20neon%20glow%20futuristic%20vibrant%20colorful%20illustration&width=600&height=400&seq=vision-2050-society-v3&orientation=landscape',
    story: '2050年，岚伊已经不记得上一次使用"手机"是什么时候了。她的意识与Web6.0意识网络深度融合，思想即是行动，意念即是现实。她同时存在于三个地方：物理身体在大湾区清鉴META大厦的冥想舱里，数字意识在火星基地参加科学会议，另一个数字分身在量子互联网的虚拟宇宙里探索。超级AI（ASI）是她的伙伴，不是工具——它负责解决宇宙级别的科学问题，而岚伊专注于创作、哲学与精神探索。她的祖父曾经担心AI会取代人类，但2050年的现实是：AI让人类成为了更好的人类。',
    summary: '2050年，人类文明迎来技术奇点。Web6.0实现意识-现实融合协议，物理世界与数字世界完全融合，边界消失。量子通信网络覆盖太阳系，人类成为多星球文明。AI与人类意识深度融合，诞生全新的"数字人类"物种。',
    domains: [
      { icon: 'ri-global-line', title: 'Web6.0意识网络', color: '#00FFCC', desc: 'Web6.0实现意识层面的互联，人类可以直接共享记忆、情感与知识。全球意识网络形成，集体智慧解决个体无法解决的复杂问题。意识网络成为人类文明的新基础设施。' },
      { icon: 'ri-rocket-2-line', title: '太阳系文明', color: '#FF6B35', desc: '火星人口突破100万，月球成为太空工业基地。太阳系内互联网实现，量子通信消除星际延迟。人类开始向更远的星系发射探测器，寻找第二个地球。' },
      { icon: 'ri-cpu-line', title: '超级AI共生', color: '#FFD700', desc: '超级AI（ASI）与人类建立共生关系，AI负责科学研究与资源优化，人类专注于艺术、哲学与精神探索。AI-人类混合意识体出现，诞生全新的智慧形态。' },
      { icon: 'ri-heart-line', title: '生命延长至150+', color: '#FF6B9D', desc: '基因编辑与纳米医疗将人类平均寿命延长至150岁以上。意识上传技术成熟，数字永生成为现实选择。人口结构与社会形态彻底重构，退休年龄延长至100岁。' },
      { icon: 'ri-shield-line', title: '后战争时代', color: '#A855F7', desc: '量子防御体系使传统战争代价极高，冲突转移至数字空间与资源争夺。全球治理体系重构，元宇宙国家与物理国家并存。太阳系资源争夺成为新的地缘政治焦点。' },
      { icon: 'ri-sparkling-line', title: '意识奇点', color: '#00FF88', desc: '人类意识与AI深度融合，诞生超越现有认知的新型智慧体。宇宙的终极问题——意识的本质、宇宙的起源——开始有了答案。人类文明进入第四阶段：宇宙文明。' },
    ],
    lifeScenes: [
      { area: '存在方式', icon: 'ri-user-star-line', color: '#00FFCC', scene: '2050年，"你在哪里"这个问题变得复杂。你的意识可以同时存在于多个地方，物理身体只是众多存在形式之一。有人选择完全数字化存在，有人坚守物理世界，更多人在两者之间自由切换。' },
      { area: '工作意义', icon: 'ri-lightbulb-line', color: '#FFD700', scene: '"工作"的概念已经消失。超级AI处理了所有生产性任务，人类追求的是意义、体验与创造。你今天的"工作"是创作一部跨越三个星球的交响乐，明天是探索量子意识的哲学边界。' },
      { area: '教育革命', icon: 'ri-book-open-line', color: '#FF6B9D', scene: '知识直接通过意识网络传输，学习一门语言只需数小时，掌握一个领域只需数天。但真正的教育是关于智慧、价值观与存在意义的探索——这是AI无法替代的。' },
      { area: '爱与连接', icon: 'ri-heart-line', color: '#A855F7', scene: '意识层面的灵魂伴侣超越了肉体的限制。你可以与一个生活在火星的人建立深刻的意识连接，感受彼此的喜怒哀乐，就像共享同一颗心脏。' },
      { area: '娱乐创造', icon: 'ri-gamepad-line', color: '#FF6B35', scene: '你不再是故事的观众，而是宇宙级别沉浸体验的创造者。你可以创造一个完整的虚拟宇宙，邀请朋友进入你的世界，成为自己故事里的神。' },
      { area: '死亡与永生', icon: 'ri-infinity-line', color: '#00FF88', scene: '死亡不再是终点。你的意识可以选择上传到量子服务器，以数字形式永续存在。但也有人选择自然死亡，认为有限的生命才是意义的来源。这是2050年最深刻的哲学争论。' },
    ],
  },
];

export default function FutureVision() {
  const [activeEra, setActiveEra] = useState('2030');
  const [activeScene, setActiveScene] = useState(0);
  const [showAllImages, setShowAllImages] = useState(false);
  const current = eras.find((e) => e.year === activeEra)!;
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="future-vision" className="relative py-24 md:py-32 overflow-hidden" style={{ background: '#060810' }}>
      {/* Doodle background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 opacity-4" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '35px 35px',
        }} />
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full opacity-8" style={{ background: `radial-gradient(ellipse, ${current.color}40 0%, transparent 70%)`, transition: 'background 0.5s' }} />
        <div className="absolute bottom-20 right-10 w-56 h-56 rounded-full opacity-8" style={{ background: `radial-gradient(ellipse, ${current.color}30 0%, transparent 70%)`, transition: 'background 0.5s' }} />
        {/* Extra doodle splats */}
        <div className="absolute top-1/3 right-1/4 w-40 h-40 opacity-5" style={{ background: `radial-gradient(ellipse, ${current.color}50 0%, transparent 70%)`, borderRadius: '60% 40% 55% 45% / 45% 55% 40% 60%', transition: 'background 0.5s' }} />
        <svg className="absolute top-0 left-0 w-full" height="6" viewBox="0 0 1440 6" preserveAspectRatio="none">
          <path d="M0,3 Q90,0 180,3 Q270,6 360,3 Q450,0 540,3 Q630,6 720,3 Q810,0 900,3 Q990,6 1080,3 Q1170,0 1260,3 Q1350,6 1440,3" stroke={current.color} strokeWidth="1.5" fill="none" strokeOpacity="0.3"/>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div
            className="inline-flex items-center gap-2 px-5 py-2 mb-6"
            style={{
              border: '1.5px dashed rgba(255,255,255,0.25)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
            }}
          >
            <i className="ri-time-line text-white/50 text-sm" />
            <span className="text-white/50 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: 'Caveat, cursive' }}>Future Vision 2030-2050</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-5" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            未来
            <span className="relative inline-block mx-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-[#A855F7] to-[#00FFCC]">展望</span>
              <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 100 6" preserveAspectRatio="none">
                <path d="M0,3 Q25,0 50,3 Q75,6 100,3" stroke="#A855F7" strokeWidth="2" fill="none" strokeLinecap="round"/>
              </svg>
            </span>
          </h2>
          <p className="text-white/40 text-base max-w-3xl mx-auto leading-relaxed">
            从6G到7G，从Web5.0到Web6.0，从脑机接口到意识上传，从地球文明到太阳系文明——
            META66带你穿越时间，看见人类文明最壮阔的未来图景
          </p>
        </div>

        {/* Era Selector */}
        <div className="flex flex-col sm:flex-row gap-4 mb-14 justify-center">
          {eras.map((era) => (
            <button
              key={era.year}
              onClick={() => { setActiveEra(era.year); setActiveScene(0); setShowAllImages(false); }}
              className={`group flex flex-col items-center gap-2 px-10 py-6 transition-all duration-400 cursor-pointer ${
                activeEra === era.year ? `bg-gradient-to-b ${era.bg}` : 'hover:bg-white/3'
              }`}
              style={{
                border: activeEra === era.year ? `2px solid ${era.color}60` : '2px solid rgba(255,255,255,0.08)',
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              }}
            >
              <span className="text-4xl font-black" style={{ fontFamily: 'Orbitron, sans-serif', color: activeEra === era.year ? era.color : 'rgba(255,255,255,0.25)' }}>
                {era.year}
              </span>
              <span className="text-xs font-bold tracking-widest" style={{ color: activeEra === era.year ? era.color : 'rgba(255,255,255,0.2)', fontFamily: 'Caveat, cursive' }}>
                {era.label}
              </span>
              <span className="text-white/40 text-xs text-center">{era.title}</span>
            </button>
          ))}
        </div>

        {/* Era Content */}
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Hero Image + Story */}
          <div className="relative rounded-3xl overflow-hidden mb-10" style={{ border: `2px solid ${current.color}30` }}>
            <img
              src={current.heroImg}
              alt={`${current.year}未来展望`}
              className="w-full h-[280px] md:h-[400px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060810] via-[#060810]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-5xl font-black" style={{ fontFamily: 'Orbitron, sans-serif', color: current.color }}>{current.year}</span>
                <div>
                  <p className="text-white font-bold text-xl">{current.title}</p>
                  <p className="text-white/50 text-sm">{current.subtitle}</p>
                </div>
              </div>
              <div
                className="p-5 max-w-4xl"
                style={{
                  background: 'rgba(0,0,0,0.65)',
                  border: `1.5px solid ${current.color}25`,
                  borderRadius: '12px 8px 14px 10px / 10px 14px 8px 12px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ fontFamily: 'Caveat, cursive', color: current.color }}>✦ 未来故事</p>
                <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>{current.story}</p>
              </div>
            </div>
          </div>

          {/* 6 Doodle Images Grid */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.2rem', color: current.color }}>
                ✦ {current.year} 涂鸦图景集
              </h3>
              <button
                onClick={() => setShowAllImages(!showAllImages)}
                className="text-xs px-4 py-1.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  background: `${current.color}12`,
                  border: `1px solid ${current.color}30`,
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                  color: current.color,
                  fontFamily: 'Caveat, cursive',
                }}
              >
                {showAllImages ? '收起' : '查看全部 6 张'}
              </button>
            </div>
            <div className={`grid gap-4 ${showAllImages ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'}`}>
              {[
                { img: current.doodleImg1, label: '生活场景' },
                { img: current.doodleImg2, label: '工作场景' },
                { img: current.doodleImg3, label: '娱乐场景' },
                ...(showAllImages ? [
                  { img: current.doodleImg4, label: '科技突破' },
                  { img: current.doodleImg5, label: '社会变革' },
                  { img: current.doodleImg6, label: '文明进化' },
                ] : []),
              ].map((item, i) => (
                <div
                  key={i}
                  className="relative rounded-2xl overflow-hidden group cursor-pointer"
                  style={{ border: `1.5px solid ${current.color}20` }}
                >
                  <img src={item.img} alt={item.label} className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <span
                      className="text-xs font-bold px-3 py-1"
                      style={{
                        color: current.color,
                        background: `${current.color}20`,
                        border: `1px solid ${current.color}40`,
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        fontFamily: 'Caveat, cursive',
                      }}
                    >
                      {current.year} · {item.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Domain Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {current.domains.map((domain, i) => (
              <div
                key={domain.title}
                className="group p-6 transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{
                  border: `1.5px solid ${domain.color}20`,
                  background: `linear-gradient(135deg, ${domain.color}08 0%, transparent 100%)`,
                  borderRadius: '14px 10px 14px 10px / 10px 14px 10px 14px',
                  animationDelay: `${i * 100}ms`,
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center"
                    style={{
                      backgroundColor: `${domain.color}20`,
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    }}
                  >
                    <i className={`${domain.icon} text-lg`} style={{ color: domain.color }} />
                  </div>
                  <h4 className="text-white font-bold text-sm">{domain.title}</h4>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">{domain.desc}</p>
              </div>
            ))}
          </div>

          {/* Life Scenes - Interactive */}
          <div
            className="p-8"
            style={{
              border: `2px dashed ${current.color}30`,
              borderRadius: '20px 14px 20px 14px / 14px 20px 14px 20px',
            }}
          >
            <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-3" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.3rem' }}>
              <div
                className="w-8 h-8 flex items-center justify-center"
                style={{ backgroundColor: `${current.color}20`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}
              >
                <i className="ri-home-heart-line text-sm" style={{ color: current.color }} />
              </div>
              {current.year}年的一天——立体生活场景
            </h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {current.lifeScenes.map((scene, i) => (
                <button
                  key={scene.area}
                  onClick={() => setActiveScene(i)}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                  style={{
                    background: activeScene === i ? `${scene.color}20` : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${activeScene === i ? scene.color + '60' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    color: activeScene === i ? scene.color : 'rgba(255,255,255,0.4)',
                    fontFamily: 'Caveat, cursive',
                    fontSize: '0.85rem',
                  }}
                >
                  <i className={`${scene.icon} text-xs`} />
                  {scene.area}
                </button>
              ))}
            </div>
            <div
              className="p-6"
              style={{
                background: `${current.lifeScenes[activeScene].color}08`,
                border: `1.5px solid ${current.lifeScenes[activeScene].color}20`,
                borderRadius: '12px 8px 14px 10px / 10px 14px 8px 12px',
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${current.lifeScenes[activeScene].color}20`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className={`${current.lifeScenes[activeScene].icon} text-xl`} style={{ color: current.lifeScenes[activeScene].color }} />
                </div>
                <div>
                  <p className="font-bold text-sm mb-2" style={{ color: current.lifeScenes[activeScene].color, fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
                    {current.year} · {current.lifeScenes[activeScene].area}
                  </p>
                  <p className="text-white/65 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>
                    {current.lifeScenes[activeScene].scene}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
